import React from "react";
import PropTypes from "prop-types";
import Select from "react-select";
import { getSelectValue } from "./utils/selectHelpers";
import Spinner from "components/Common/Spinner";

const UploadTemplateForm = ({
  formData,
  languages,
  errors,
  jsonError,
  isSubmitting,
  handleChange,
  handleLanguageChange,
  handleSubmit,
  baseHtmlFileRef,
  placeholdersJsonFileRef,
}) => {
  const { name, subject, type, base_html, placeholders_json, target_languages, hasConsented } = formData;
  const baseHtmlFileName = base_html?.name || "";
  const translationJsonFileName = placeholders_json?.name || "";

  return (
    <>
      <Spinner isActive={isSubmitting} text="Submitting..." />
      <form onSubmit={handleSubmit} noValidate className="needs-validation">
        {jsonError && (
          <div className="alert alert-danger" role="alert">
            {jsonError}
          </div>
        )}

        <div className="mb-3">
          <label htmlFor="templateType" className="form-label">Template Type</label>
          <select
            id="templateType"
            name="type"
            value={type}
            onChange={handleChange}
            required
            className={`form-select ${errors.type ? "is-invalid" : ""}`}
          >
            <option value="">Select Type</option>
            <option value="landing_page">Landing Page</option>
            <option value="email_template">Email Template</option>
          </select>
          {errors.type && <div className="invalid-feedback text-start">{errors.type}</div>}
        </div>

        <div className="mb-3">
          <label htmlFor="templateName" className="form-label">Template Name</label>
          <input
            type="text"
            id="templateName"
            name="name"
            value={name}
            onChange={handleChange}
            placeholder="e.g., Office 365 Login Alert"
            className={`form-control ${errors.name ? "is-invalid" : ""}`}
          />
          {errors.name && <div className="invalid-feedback text-start">{errors.name}</div>}
        </div>

        <div className="mb-3">
          <label htmlFor="emailSubject" className="form-label">
            Email Subject {type === "email_template" && <span className="text-danger">*</span>}
          </label>
          <input
            type="text"
            id="emailSubject"
            name="subject"
            value={subject}
            onChange={handleChange}
            placeholder="e.g., Important Security Alert"
            className={`form-control ${errors.subject ? "is-invalid" : ""}`}
          />
          {errors.subject && <div className="invalid-feedback text-start">{errors.subject}</div>}
        </div>

        <div className="mb-3">
          <label className="form-label">Base Template File</label>
          <div className="file-upload-box">
            <input
              type="file"
              id="baseHtmlFile"
              name="base_html"
              onChange={handleChange}
              className="file-input"
              accept=".html,text/html"
              ref={baseHtmlFileRef}
            />
            <label htmlFor="baseHtmlFile" className="file-upload-label">
              {baseHtmlFileName || "Click to upload."}
            </label>
          </div>
          {errors.base_html && <div className="invalid-feedback d-block text-start">{errors.base_html}</div>}
        </div>
        <div className="mb-3">
          <label className="form-label">Translation JSON File</label>
          <div className="file-upload-box">
            <input
              type="file"
              id="placeholdersJsonFile"
              name="placeholders_json"
              onChange={handleChange}
              className="file-input"
              accept=".json,application/json"
              ref={placeholdersJsonFileRef}
            />
            <label htmlFor="placeholdersJsonFile" className="file-upload-label">
              {translationJsonFileName || "Click to upload."}
            </label>
          </div>
          {errors.placeholders_json && <div className="invalid-feedback d-block text-start">{errors.placeholders_json}</div>}
        </div>

        <div className="mb-3">
          <label className="form-label">Target Languages</label>
          <Select
            isMulti
            options={languages}
            value={getSelectValue(languages, target_languages)}
            onChange={handleLanguageChange}
            classNamePrefix="react-select"
            className={errors.target_languages ? "is-invalid" : ""}
            menuPlacement="auto"
            menuPortalTarget={document.body}
          />
          {errors.target_languages && <div className="invalid-feedback d-block text-start">{errors.target_languages}</div>}
        </div>

        <div className="mb-3 form-check text-start">
          <input
            type="checkbox"
            id="hasConsented"
            name="hasConsented"
            checked={hasConsented}
            onChange={handleChange}
            className={`form-check-input ${errors.hasConsented ? "is-invalid" : ""}`}
          />
          <label htmlFor="hasConsented" className="form-check-label">
            Confirm assets are uploaded and links are updated with correct values<span className="text-danger">*</span>
          </label>
          {errors.hasConsented && <div className="invalid-feedback d-block text-start">{errors.hasConsented}</div>}
        </div>

        <button type="submit" className="btn btn-primary mt-2" disabled={isSubmitting}>
          Generate Templates
        </button>
      </form>
    </>
  );
};

UploadTemplateForm.propTypes = {
  formData: PropTypes.object.isRequired,
  languages: PropTypes.array.isRequired,
  errors: PropTypes.object.isRequired,
  jsonError: PropTypes.string,
  isSubmitting: PropTypes.bool.isRequired,
  handleChange: PropTypes.func.isRequired,
  handleLanguageChange: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  baseHtmlFileRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.instanceOf(Element) })]),
  placeholdersJsonFileRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.instanceOf(Element) })]),
};

export default UploadTemplateForm;